import { Link } from "react-router-dom";
import { LogIn, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { QuantityStepper } from "@/components/QuantityStepper";
import { useSite } from "@/context/SiteContext";
import { SUPPLIER_LEAD_DAYS, formatCurrency, marginInfo, packSize, recommendedPrice, stockLevel, unitLabel } from "@/lib/product";

export const OWN_STOCK_LEAD_DAYS = 3;

const STIP = {
  in: "bg-[hsl(var(--success))]",
  limited: "bg-[hsl(var(--success))]",
  supplier: "bg-[hsl(var(--warning))]",
  out: "bg-[hsl(var(--danger))]",
  unavailable: "bg-muted-foreground",
};

/** "Op voorraad · binnen 3 werkdagen geleverd" e.d. */
export const VoorraadRegel = ({ product, ctx }) => {
  const level = stockLevel(product);
  const tekst =
    level === "supplier"
      ? ctx.t("pdp.stockSupplier", { dagen: SUPPLIER_LEAD_DAYS })
      : level === "out"
        ? ctx.t("stock.backorder")
        : level === "unavailable"
          ? ctx.t("stock.unavailable")
          : ctx.t(level === "limited" ? "pdp.stockLimited" : "pdp.stockIn", { dagen: OWN_STOCK_LEAD_DAYS });
  return (
    <p className="flex items-center gap-2 text-sm" data-testid="pdp-stock-line" data-stock-level={level}>
      <span className={`h-2 w-2 shrink-0 rounded-full ${STIP[level]}`} aria-hidden="true" />
      <span className={level === "out" || level === "unavailable" ? "text-muted-foreground" : ""}>{tekst}</span>
    </p>
  );
};

export const totaalLabel = (product, aantal, ctx) => ctx.t("pdp.buy");

const NietIngelogd = ({ ctx }) => (
  <div className="space-y-3" data-testid="pdp-login-prompt">
    <p className="text-[15px] leading-7 text-foreground/70">{ctx.t("pdp.loginForPrices")}</p>
    <div className="flex flex-wrap gap-2">
      <Button asChild data-testid="pdp-login-button">
        <Link to="/login"><LogIn className="mr-2 h-4 w-4" aria-hidden="true" />{ctx.t("nav.login")}</Link>
      </Button>
      <Button variant="outline" asChild data-testid="pdp-register-button">
        <Link to="/registreren"><UserPlus className="mr-2 h-4 w-4" aria-hidden="true" />{ctx.t("pdp.requestAccount")}</Link>
      </Button>
    </div>
  </div>
);

export const Koopblok = ({ product, ctx }) => {
  const { instellingen } = useSite();
  if (!ctx.account) return <NietIngelogd ctx={ctx} />;

  const marge = marginInfo(product, ctx.account);
  const advies = recommendedPrice(product);
  const pack = packSize(product) || 1;
  const korting = Number(instellingen?.korting_percentage ?? 0);
  const direct = Boolean(instellingen?.direct_betalen) && korting > 0;

  return (
    <div className="space-y-4" data-testid="pdp-block-koopblok">
      <div data-testid="pdp-price">
        <p className="flex flex-wrap items-baseline gap-x-2">
          <span className="text-2xl font-semibold tabular-nums" data-testid="pdp-price-unit">{formatCurrency(product?.verkoopprijs, ctx.lang)}</span>
          <span className="text-xs text-muted-foreground">{ctx.t("price.exclVat")}</span>
          {marge && (
            <span className="ml-auto rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary" data-testid="pdp-price-margin">
              {ctx.t("pdp.margin", { pct: marge.percentage })}
            </span>
          )}
        </p>
        {advies !== null && (
          <p className="mt-0.5 text-xs text-muted-foreground" data-testid="pdp-price-rrp">{formatCurrency(advies, ctx.lang)} {ctx.t("price.rrp")}</p>
        )}
      </div>

      <VoorraadRegel product={product} ctx={ctx} />

      {!ctx.deliverable && (
        <p className="text-xs text-muted-foreground" data-testid="product-country-note">{ctx.t("stock.countryBlocked")}</p>
      )}
      {ctx.deliverable && !ctx.orderable && (
        <p className="text-xs text-muted-foreground" data-testid="product-unavailable-note">{ctx.t("stock.unavailableNote")}</p>
      )}

      {ctx.orderable ? (
        <div className="space-y-2">
          <div className="hidden items-center gap-2 sm:flex">
            <QuantityStepper product={product} value={ctx.aantal} onChange={ctx.setAantal} inputId="product-quantity" testIdPrefix="product-quantity" />
            <Button className="h-11 min-w-0 flex-1 whitespace-normal" onClick={ctx.onAdd} data-testid="product-add-to-cart-button">
              {totaalLabel(product, ctx.aantal, ctx)}
            </Button>
          </div>
          <p className="text-xs leading-5 text-muted-foreground/80" data-testid="pdp-payment-line">
            {ctx.t("pdp.perPack", { aantal: pack, eenheid: unitLabel(product?.eenheid || ctx.t("cart.pieces"), pack, ctx.lang) })}
            {" · "}
            {direct ? ctx.t("pdp.paymentLine", { korting }) : ctx.t("pdp.paymentLineInvoice")}
          </p>
        </div>
      ) : (
        <Button className="h-11 w-full" disabled data-testid="product-add-to-cart-button">
          {ctx.deliverable ? ctx.t("stock.unavailable") : ctx.t("stock.countryBlockedShort")}
        </Button>
      )}
    </div>
  );
};

/** Vaste koopbalk onderaan op mobiel. */
export const MobielKoopbalk = ({ product, ctx }) => {
  if (!ctx.account || !ctx.orderable) return null;
  return (
    <div className="fixed inset-x-0 bottom-0 z-40 border-t bg-background/95 px-4 py-3 shadow-[0_-8px_24px_-12px_rgba(0,0,0,0.25)] backdrop-blur sm:hidden" data-testid="pdp-mobile-buybar">
      <div className="flex items-center gap-2">
        <QuantityStepper product={product} value={ctx.aantal} onChange={ctx.setAantal} inputId="product-quantity-mobile" testIdPrefix="product-quantity-mobile" />
        <Button className="h-11 min-w-0 flex-1 whitespace-normal px-3 text-sm leading-tight" onClick={ctx.onAdd} data-testid="pdp-mobile-add-to-cart-button">
          {totaalLabel(product, ctx.aantal, ctx)}
        </Button>
      </div>
    </div>
  );
};
